import CloseIcon from '@mui/icons-material/Close'
import { Alert, Button, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../app/hooks'
import { useGetVerifiedDomainsQuery } from '../../app/redux-fetch/apiQuery'
import rootEnum from '../../components/Routes/rootEnum'
import { VerifiedDomainStatus } from '../CommonSettings/VerifiedDomains/types'
import { fetchLoggedInUserSelector, getIsCustomerUserTypeSelector } from '../selectors'
import messages from './messages'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

// The single "verify your domain" nudge for newly-signed-up Tenant Account Owners (E4 F4-6 / WI 508,
// unifying E3's WI 500 dashboard banner). Shows only when (signed up < 7 days ago) AND (no Verified
// domain yet) AND (not dismissed on this device). Piggybacks on the existing list endpoint (no new API).
// Customer users never see it. Dismissal is per-tenant and shared with anywhere else this card renders.
const VerifyDomainCard = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data)
  const isCustomerUser = useAppSelector(getIsCustomerUserTypeSelector)
  const tenantId = loggedInUser?.tenantId

  const { data: domains = [], isSuccess } = useGetVerifiedDomainsQuery(
    { tenantId: tenantId as number },
    { skip: !tenantId || isCustomerUser },
  )

  const dismissKey = `verified-domain-banner-dismissed:${tenantId ?? 0}`
  const [dismissed, setDismissed] = useState(false)

  // tenantId is undefined on first render (logged-in user loads async), so the dismissal can't be read
  // until it resolves. Re-read the per-tenant key whenever tenantId changes, otherwise a
  // previously-dismissed card would reappear once the user data arrives.
  useEffect(() => {
    if (!tenantId) return
    setDismissed(localStorage.getItem(dismissKey) === 'true')
  }, [tenantId, dismissKey])

  const shouldShow = useMemo(() => {
    if (!tenantId || isCustomerUser || dismissed) return false
    if (!isSuccess) return false // don't flash the card before the verified-domains query resolves
    if (!loggedInUser?.tenantAcceptanceDate) return false // pre-E4 tenant => no window; stay hidden
    const ageMs = Date.now() - dayjs(loggedInUser.tenantAcceptanceDate).valueOf()
    if (ageMs < 0 || ageMs > SEVEN_DAYS_MS) return false
    return !domains.some((d) => d.status === VerifiedDomainStatus.Verified)
  }, [tenantId, isCustomerUser, dismissed, isSuccess, loggedInUser, domains])

  if (!shouldShow) return null

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, 'true')
    setDismissed(true)
  }

  return (
    <Alert
      severity='info'
      sx={{ mb: 2 }}
      action={
        <>
          <Button color='inherit' size='small' onClick={() => navigate(rootEnum.VERIFIED_DOMAINS)}>
            {formatMessage(messages.verifyDomainCta)}
          </Button>
          <IconButton aria-label='Close' color='inherit' size='small' onClick={handleDismiss}>
            <CloseIcon fontSize='inherit' />
          </IconButton>
        </>
      }
    >
      {formatMessage(messages.verifyDomainBannerBody)}
    </Alert>
  )
}

export default VerifyDomainCard
